// Well-known OpenAI-compatible providers, for the "OpenAI-compatible API"
// connection form's provider combobox: picking one fills the hidden base
// URL; "Custom" leaves it to the address field.
//
// Base URLs checked 2026-10-01 against each provider's own docs (linked as
// `docsUrl`); only entries confirmed there are listed. Skipped: Azure OpenAI
// (its base URL is per-deployment, not fixed — use Custom) and OVHcloud AI
// Endpoints (its docs give a per-model endpoint, not one fixed URL).

export interface OpenAiCompatibleProvider {
  id: string
  name: string
  /** The base URL an OpenAI SDK would be pointed at, e.g. ending in `/v1`. */
  baseUrl: string
  /** Where to read about the API and get a key. */
  docsUrl: string
  /** Shown as the API key field's placeholder, when the shape is known. */
  keyPlaceholder?: string
}

export const OPENAI_COMPATIBLE_PROVIDERS: OpenAiCompatibleProvider[] = [
  {
    id: 'openrouter',
    name: 'OpenRouter',
    baseUrl: 'https://openrouter.ai/api/v1',
    docsUrl: 'https://openrouter.ai/docs/api_reference/authentication',
    keyPlaceholder: 'sk-or-…',
  },
  {
    id: 'groq',
    name: 'Groq',
    baseUrl: 'https://api.groq.com/openai/v1',
    docsUrl: 'https://console.groq.com/docs/overview',
    keyPlaceholder: 'gsk_…',
  },
  {
    id: 'together',
    name: 'Together AI',
    baseUrl: 'https://api.together.xyz/v1',
    docsUrl: 'https://docs.together.ai',
  },
  {
    id: 'fireworks',
    name: 'Fireworks AI',
    baseUrl: 'https://api.fireworks.ai/inference/v1',
    docsUrl: 'https://docs.fireworks.ai/tools-sdks/openai-compatibility',
  },
  {
    id: 'deepinfra',
    name: 'DeepInfra',
    baseUrl: 'https://api.deepinfra.com/v1/openai',
    docsUrl: 'https://docs.deepinfra.com/',
  },
  {
    id: 'mistral',
    name: 'Mistral',
    baseUrl: 'https://api.mistral.ai/v1',
    docsUrl: 'https://docs.mistral.ai/api/',
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    baseUrl: 'https://api.deepseek.com',
    docsUrl: 'https://api-docs.deepseek.com/',
    keyPlaceholder: 'sk-…',
  },
  {
    id: 'xai',
    name: 'xAI (Grok)',
    baseUrl: 'https://api.x.ai/v1',
    docsUrl: 'https://docs.x.ai/developers/quickstart',
    keyPlaceholder: 'xai-…',
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    baseUrl: 'https://api.perplexity.ai',
    docsUrl: 'https://docs.perplexity.ai/guides/chat-completions-guide',
    keyPlaceholder: 'pplx-…',
  },
  {
    id: 'cerebras',
    name: 'Cerebras',
    baseUrl: 'https://api.cerebras.ai/v1',
    docsUrl: 'https://inference-docs.cerebras.ai/resources/openai',
  },
  {
    id: 'google-gemini',
    name: 'Google Gemini',
    baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai/',
    docsUrl: 'https://ai.google.dev/gemini-api/docs/openai',
    keyPlaceholder: 'AIza…',
  },
  {
    id: 'huggingface',
    name: 'Hugging Face Inference Providers',
    baseUrl: 'https://router.huggingface.co/v1',
    docsUrl: 'https://huggingface.co/docs/inference-providers/index',
    keyPlaceholder: 'hf_…',
  },
  {
    id: 'nebius',
    name: 'Nebius',
    baseUrl: 'https://api.tokenfactory.nebius.com/v1/',
    docsUrl: 'https://docs.tokenfactory.nebius.com/quickstart',
  },
  {
    id: 'novita',
    name: 'Novita',
    baseUrl: 'https://api.novita.ai/openai',
    docsUrl: 'https://docs.novita.ai/guides/llm-api',
  },
  {
    id: 'hyperbolic',
    name: 'Hyperbolic',
    baseUrl: 'https://api.hyperbolic.xyz/v1',
    docsUrl: 'https://docs.hyperbolic.ai/docs/inference/overview',
  },
  {
    id: 'scaleway',
    name: 'Scaleway',
    baseUrl: 'https://api.scaleway.ai/v1',
    docsUrl:
      'https://www.scaleway.com/en/docs/generative-apis/reference-content/openai-compatibility/',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    baseUrl: 'https://api.openai.com/v1',
    docsUrl: 'https://platform.openai.com/docs/api-reference',
    keyPlaceholder: 'sk-…',
  },
]
